import { useState } from 'react'
import type { PaymentView, ShopifyOrderSummary } from '@fitting-room/contracts'
import { normalizeShopifyOrderNo } from '@fitting-room/contracts/order-math'
import { useSetPaymentShopifyOrderNo, useVoidPayment } from '../../../lib/adminQueries'
import { BOUTIQUE_LOCALE, boutiqueDateTime } from '../../../lib/boutique'
import { formatDay } from '../../../lib/format'
import { CANNOT_VOID, PAYMENT_KIND_LABEL, PAYMENT_METHOD_LABEL, paymentName, shopifyOrderNoLabel, signedAmount } from '../../../lib/payments'
import { useStaffToken } from '../../../lib/staffToken'
import { ReasonDialog } from '../orders/ReasonDialog'
import { ShopifyOrderField } from './ShopifyOrderField'

/**
 * An order's payments, oldest first: tperent's `PaymentLedger`. Each row is
 * the day the money moved, what it was, how, the amount (a refund with a
 * minus), who recorded it and when, and a back-dated mark when those two
 * days differ. A voided row stays, struck through, with who voided it and
 * why — there is no edit and no delete, only void (`docs/22` § "Payments").
 */
export function PaymentLedger({ payments }: { readonly payments: readonly PaymentView[] }) {
  const { token } = useStaffToken()
  const voidPayment = useVoidPayment(token)
  const [voiding, setVoiding] = useState<PaymentView | null>(null)
  // The void button is greyed out rather than hidden, and pressing it says why
  // on the row: a tablet at the counter has no hover for a tooltip.
  const [explained, setExplained] = useState<string | null>(null)

  if (payments.length === 0) {
    return <p className="st-meta py-3 text-sm">No payments recorded yet.</p>
  }

  return (
    <>
      <ul className="divide-y divide-[color:var(--st-rule)]" aria-label="Payments">
        {payments.map((payment) => {
          const voided = Boolean(payment.voidedAt)
          return (
            <li key={payment.id} className="py-2.5 text-sm" data-payment-id={payment.id} data-voided={voided || undefined}>
              <div className="flex items-start gap-3">
                <div className={`min-w-0 flex-1 ${voided ? 'st-voided' : ''}`}>
                  <span className="tabular-nums">{formatDay(payment.paidAt, BOUTIQUE_LOCALE)}</span>
                  <span className="font-medium"> · {PAYMENT_KIND_LABEL[payment.kind]}</span>
                  <span className="st-meta"> · {PAYMENT_METHOD_LABEL[payment.method]}</span>
                  {payment.reference ? <span className="st-meta"> · {payment.reference}</span> : null}
                </div>
                <span className={`shrink-0 font-medium tabular-nums ${voided ? 'st-voided' : ''}`}>{signedAmount(payment)}</span>
                {!voided ? (
                  <button
                    type="button"
                    className="st-btn-quiet shrink-0 text-xs"
                    aria-disabled={!payment.canVoid}
                    style={payment.canVoid ? undefined : { opacity: 0.45 }}
                    onClick={() => (payment.canVoid ? setVoiding(payment) : setExplained(payment.id))}
                  >
                    Void
                  </button>
                ) : null}
              </div>
              <div className="st-meta mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs">
                <span>
                  Recorded by {payment.recordedByLabel}, {boutiqueDateTime(payment.createdAt)}
                </span>
                {payment.backdated ? <span className="st-badge st-badge--open">Back-dated</span> : null}
                {!voided || payment.shopifyOrderNo ? <ShopifyOrderNo payment={payment} label={shopifyOrderNoLabel(payment, payments)} /> : null}
                {payment.note ? <span>· {payment.note}</span> : null}
              </div>
              {explained === payment.id && !payment.canVoid ? (
                <p className="mt-1 text-xs" style={{ color: '#7a5a1a' }} role="status">
                  {CANNOT_VOID}
                </p>
              ) : null}
              {voided ? (
                <p className="mt-0.5 text-xs" style={{ color: '#96432f' }}>
                  Voided by {payment.voidedByLabel ?? 'someone'}, {boutiqueDateTime(payment.voidedAt as string)}: {payment.voidReason}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
      <ReasonDialog
        open={voiding !== null}
        title={voiding ? `Void the ${PAYMENT_KIND_LABEL[voiding.kind].toLowerCase()} of ${signedAmount(voiding).replace('−', '')}?` : ''}
        placeholder="Like: rang it up twice, wrong amount"
        confirmLabel="Void payment"
        pendingLabel="Voiding…"
        tone="alert"
        pending={voidPayment.isPending}
        error={voidPayment.error?.message}
        footnote="It stays on the payment, with your name and the time."
        onClose={() => {
          voidPayment.reset()
          setVoiding(null)
        }}
        onConfirm={(reason) => voiding && voidPayment.mutate({ paymentId: voiding.id, reason }, { onSuccess: () => setVoiding(null) })}
      >
        It stays on the ledger, struck through, and stops counting toward what&rsquo;s been paid. If it was a mistake, record
        the right one after.
      </ReasonDialog>
    </>
  )
}

/**
 * The Shopify order number the payment was rung up under — the deposit's
 * "Initial order #", the balance's "Balance order #" (`label`) — and the one
 * thing about a payment that can be typed in or corrected after it is
 * recorded (`docs/22` § "Payments"). Read-only on a voided payment. Its
 * buttons name the payment: a ledger has one "Edit" per row, and a screen
 * reader lists them all out of context.
 */
function ShopifyOrderNo({ payment, label }: { readonly payment: PaymentView; readonly label: string }) {
  const { token } = useStaffToken()
  const set = useSetPaymentShopifyOrderNo(token)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(payment.shopifyOrderNo ?? '')
  const [picked, setPicked] = useState<ShopifyOrderSummary | null>(null)
  const voided = Boolean(payment.voidedAt)

  if (!editing) {
    return (
      <span className="flex items-center gap-1" data-testid="payment-shopify-no">
        {payment.shopifyOrderNo ? <span>· {`${label}${payment.shopifyOrderNo}`}</span> : null}
        {!voided ? (
          <button
            type="button"
            className="st-btn-quiet px-1 text-xs"
            aria-label={`${payment.shopifyOrderNo ? 'Edit' : 'Add'} the ${lowerFirst(label)} on ${paymentName(payment)}`}
            onClick={() => {
              setDraft(payment.shopifyOrderNo ?? '')
              setPicked(null)
              set.reset()
              setEditing(true)
            }}
          >
            {payment.shopifyOrderNo ? 'Edit' : `· Add ${lowerFirst(label)}`}
          </button>
        ) : null}
      </span>
    )
  }
  return (
    <form
      className="mt-1 flex w-full flex-wrap items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault()
        set.mutate({ paymentId: payment.id, shopifyOrderNo: draft.trim() || null }, { onSuccess: () => setEditing(false) })
      }}
    >
      <div className="w-full max-w-xl" aria-label={`${label.replace(' #', ' number')}, ${paymentName(payment)}`} role="group">
        <ShopifyOrderField
          picked={picked}
          typed={draft}
          startTyping={Boolean(payment.shopifyOrderNo)}
          onPick={(order) => {
            setPicked(order)
            setDraft(order ? (normalizeShopifyOrderNo(order.name) ?? '') : '')
          }}
          onType={(text) => {
            setPicked(null)
            setDraft(text)
          }}
        />
      </div>
      <button type="submit" className="st-btn-ghost py-1 text-xs" disabled={set.isPending}>
        {set.isPending ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="st-btn-quiet py-1 text-xs" onClick={() => setEditing(false)}>
        Cancel
      </button>
      {set.isError ? (
        <span className="w-full text-xs" style={{ color: '#8d3f2c' }} role="alert">
          {set.error.message}
        </span>
      ) : null}
    </form>
  )
}

/** "Initial order #" → "initial order #"; "Shopify order #" stays a name. */
function lowerFirst(label: string): string {
  return label.startsWith('Shopify') ? label : label.charAt(0).toLowerCase() + label.slice(1)
}
